'use client';
import type { JSX } from 'react';

import clsx from 'clsx';
import { useTheme } from 'next-themes';

import { testIds } from '@/components/constants/testIds';
import type { IconType } from '@/components/types/IconType';
import { appConfig } from '@/config/appConfig';
import appIcons from '@/constants/appIcons';

import Icon from '../Icon';

type ThemeSwitcherProps = {
    className?: string;
};

export default function ThemeSwitcher({ className }: ThemeSwitcherProps): JSX.Element | null {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { theme, setTheme } = useTheme();

    const containerClasses = 'inline-flex w-auto justify-around gap-2 rounded-full p-1 border border-slate-100 dark:border-neutral-800';
    const themeClasses =
        'flex justify-center align-middle items-center size-8 text-slate-800 dark:text-neutral-200 hover:text-accent-500 dark:hover:text-accent-300';
    const isActiveClasses =
        'bg-accent-500/10 dark:bg-accent-950 rounded-full text-accent-500 dark:text-accent-200 *:text-accent-500 dark:*:text-accent-200';

    const themeIcon: Record<string, IconType> = {
        system: appIcons.outline.system,
        light: appIcons.outline.sun,
        dark: appIcons.outline.moon,
    };

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div data-testid={testIds.themeSwitcher.base} className={clsx(containerClasses, className)}>
            <div className="flex gap-1 shrink mx-auto">
                {appConfig.appearance.themes.map((item) => (
                    <button
                        key={item}
                        type="button"
                        className={clsx(item === theme && isActiveClasses, themeClasses)}
                        onClick={() => setTheme(item)}
                    >
                        <Icon icon={themeIcon[item]} size={4} />
                    </button>
                ))}
            </div>
        </div>
    );
}
